import { ContentState, EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

const escapeHtmlAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const imageEntityTransform = (entity: any) => {
  if (entity.type !== 'IMAGE') return undefined;

  const { src, alt, height, width, alignment } = entity.data;

  const pixelWidth = /^\d+(\.\d+)?px$/.test(width ?? '') ? Math.round(parseFloat(width)) : undefined;
  const widthAttr = pixelWidth ? ` width="${pixelWidth}"` : '';

  const style = `max-width:100%;height:${height || 'auto'};${width ? `width:${width};` : ''}`;

  const img = `<img src="${escapeHtmlAttribute(src ?? '')}" alt="${escapeHtmlAttribute(
    alt ?? ''
  )}"${widthAttr} style="${style}"/>`;

  return alignment ? `<div style="text-align:${alignment};">${img}</div>` : img;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const draftContentToHtml = (draft: any) => {
  if (!draft) return;

  const html = trimAndRemoveLineBreaks(
    draftToHtml(convertToRaw(draft.getCurrentContent()), undefined, undefined, imageEntityTransform)
  );

  if (html === '<br>') return;

  return html;
};

const IMAGE_ALIGNMENTS = ['left', 'center', 'right'];

const customImageChunkRenderer = (nodeName: string, node: HTMLElement) => {
  let image: HTMLImageElement | undefined;
  let alignmentSource: HTMLElement | undefined;

  if (nodeName === 'img' && node instanceof HTMLImageElement) {
    image = node;
    alignmentSource = node.parentElement ?? undefined;
  } else if (nodeName === 'div' && node.children.length === 1 && node.children[0] instanceof HTMLImageElement) {
    image = node.children[0] as HTMLImageElement;
    alignmentSource = node;
  }

  if (!image) return undefined;

  const parentAlignment = alignmentSource?.style.textAlign.toLowerCase();
  const alignment = IMAGE_ALIGNMENTS.includes(parentAlignment ?? '') ? parentAlignment : image.style.float || undefined;

  return {
    type: 'IMAGE',
    mutability: 'MUTABLE',
    data: {
      src: image.getAttribute('src') || image.src,
      alt: image.alt,
      height: image.style.height,
      width: image.style.width,
      ...(alignment ? { alignment } : {})
    }
  };
};

export const htmlContentToDraft = (html: string | null | undefined) => {
  if (!html) return EditorState.createEmpty();

  const contentBlock = htmlToDraft(html, customImageChunkRenderer);
  const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap);
  return EditorState.createWithContent(contentState);
};

export const trimAndRemoveLineBreaks = (string: string) => {
  if (!string) return;

  const trimmed = string.trim();

  return trimmed === '<p></p>' ? '<br>' : trimmed;
};

export const toolbarConfig = {
  options: ['inline', 'fontSize', 'fontFamily', 'image', 'colorPicker', 'list', 'link'],
  inline: {
    options: ['bold', 'italic', 'underline']
  },
  fontFamily: {
    options: ['Poppins']
  },
  link: {
    defaultTargetOption: '_blank'
  },
  colorPicker: {
    colors: ['#0f3a5d', '#e8eef9', '#ec7c2f', '#86c441', '#ec2f2f', '#000000', '#ffffff']
  },
  image: {
    urlEnabled: true,
    uploadEnabled: true,
    previewImage: true,
    alignmentEnabled: true,
    inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/webp',
    alt: { present: true, mandatory: false },
    defaultSize: { height: 'auto', width: 'auto' }
  }
};
